"use client";

import { useEffect, useContext, useState } from "react";
import { AppContext } from "../context/AppContext";
import api from "../../services/api";
import { useNavigate } from "react-router-dom";
import { saveAs } from "file-saver";
import { toast } from "react-hot-toast";

const ShowStudentForm = () => {
  const navigate = useNavigate();
  const {
    studentInfo,
    setStudentInfo,
    loginUserEmail,
    studentDetails,
    setStudentDetails,
    academicRecords,
    setAcademicRecords,
    entryTestRecords,
    setEntryTestRecords,
  } = useContext(AppContext);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingForm, setIsDownloadingForm] = useState(false);

  // Function to format the date
  const formatDate = (dateString) => {
    const date = new Date(dateString); // Convert string to Date object
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };
  const token = localStorage.getItem("token"); // Get token from localStorage

  useEffect(() => {
    const fetchStudentInfo = async () => {
      try {
        const response = await api.get(
          `/students/student-info?email=${loginUserEmail}`
        );
        setStudentDetails(response.data);

        // Fetch entry test data
        try {
          const entryTestResponse = await api.get(
            `/students/entry-test-data?email=${loginUserEmail}`
          );
          if (entryTestResponse.data) {
            setEntryTestRecords(entryTestResponse.data);
          }
        } catch (error) {
          console.error("Error fetching entry test data:", error);
        }
      } catch (error) {
        console.error("Error fetching student info:", error);
      }
    };
    fetchStudentInfo();
  }, [loginUserEmail]);

  // Function to generate student form as HTML
  const generateStudentFormHTML = () => {
    if (!studentDetails) return "";

    // Create HTML content for the student form
    const htmlContent = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Student Form - ${studentDetails.email}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
        .header { background-color: #1e40af; color: white; padding: 15px; margin-bottom: 20px; }
        .section { margin-bottom: 20px; }
        .section-title { background-color: #1e40af; color: white; padding: 10px; margin-bottom: 10px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { padding: 8px; text-align: left; border-bottom: 1px solid #ddd; }
        th { font-weight: bold; }
        .student-image { width: 150px; height: 150px; object-fit: cover; margin: 10px 0; }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>Student Information Form</h1>
      </div>
       
      
     <div class="section">
  <div class="section-title">Program Applied For</div>
  
  <div style="display: flex; align-items: flex-start; justify-content: space-between; gap: 20px;">
    
    <!-- Left Side: Table -->
    <div style="flex: 1;">
      <table>
        <tr>
          <th>LEVEL</th>
          <td>${studentDetails.level}</td>
        </tr>
        <tr>
          <th>PROGRAM (First Choice)</th>
          <td>${studentDetails.program}</td>
        </tr>
        <tr>
          <th>CURRENT SESSION</th>
          <td>${studentDetails.session}</td>
        </tr>
        
        <tr>
          <th>SECOND CHOICE</th>
          <td>${studentDetails.secondChoice || "N/A"}</td>
        </tr>
        ${
          studentDetails.level === "Undergraduate Programs"
            ? `
        <tr>
          <th>THIRD CHOICE</th>
          <td>${studentDetails.thirdChoice || "N/A"}</td>
        </tr>
        <tr>
          <th>FOURTH CHOICE</th>
          <td>${studentDetails.fourthChoice || "N/A"}</td>
        </tr>
        `
            : ""
        }
      </table>
    </div>

    <!-- Right Side: Image -->
    <div style="flex-shrink: 0; text-align: center;">
      <img src="data:image/jpeg;base64,STUDENT_IMAGE_PLACEHOLDER" alt="Student Photo" class="student-image" style="width: 150px; height: 150px; object-fit: cover; border: 3px solid #1e40af;">
    </div>

  </div>
</div>

      
      <div class="section">
        <div class="section-title">Student's Personal Information</div>
       
        <table>
          <tr>
            <th>Name</th>
            <td>${studentDetails.name}</td>
            <th>Father Name</th>
            <td>${studentDetails.fatherName}</td>
          </tr>
          <tr>
            <th>Gender</th>
            <td>${studentDetails.gender}</td>
            <th>Marital Status</th>
            <td>${studentDetails.maritalStatus}</td>
          </tr>
          <tr>
            <th>CNIC</th>
            <td>${studentDetails.cnic}</td>
            <th>Mobile No.</th>
            <td>${studentDetails.mobileNo}</td>
          </tr>
          <tr>
            <th>Date of Birth</th>
            <td>${formatDate(studentDetails.dob)}</td>
            <th>Email</th>
            <td>${studentDetails.email}</td>
          </tr>
          <tr>
            <th>Religion</th>
            <td>${studentDetails.religion}</td>
            <th>Blood Group</th>
            <td>${studentDetails.bloodGroup}</td>
          </tr>
        </table>
      </div>
      
      <div class="section">
        <div class="section-title">Address & Guardian Information</div>
        <table>
          <tr>
            <th>Address</th>
            <td>${studentDetails.address}</td>
            <th>Country</th>
            <td>${studentDetails.country}</td>
          </tr>
          <tr>
            <th>Province</th>
            <td>${studentDetails.province}</td>
            <th>City</th>
            <td>${studentDetails.city}</td>
          </tr>
          <tr>
            <th>Guardian Name</th>
            <td>${studentDetails.guardianName}</td>
            <th>Guardian Occupation</th>
            <td>${studentDetails.guardianOccupation}</td>
          </tr>
          <tr>
            <th>Guardian CNIC</th>
            <td>${studentDetails.guardianCnic}</td>
            <th>Guardian Mobile No.</th>
            <td>${studentDetails.guardianMobileNo}</td>
          </tr>
        </table>
      </div>
      
      <div class="section">
        <div class="section-title">Academic Background</div>
        <table>
          <thead>
            <tr>
              <th>Degree</th>
              <th>Major Subjects</th>
              <th>Total Marks</th>
              <th>Obtained Marks</th>
              <th>Percentage</th>
              <th>Year</th>
              <th>Board/Institution</th>
            </tr>
          </thead>
          <tbody>
            ${academicRecords
              .map(
                (record) => `
              <tr>
                <td>${record.degree}</td>
                <td>${record.majorSubjects}</td>
                <td>${record.totalMarks}</td>
                <td>${record.obtainedMarks}</td>
                <td>${record.percentage}%</td>
                <td>${record.passingYear}</td>
                <td>${record.boardInstitution}</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
      </div>

      <div class="section">
        <div class="section-title">Entry Test Result</div>
        <table>
          <thead>
            <tr>
              <th>Test Type</th>
              <th>Obtained Marks</th>
              <th>Total Marks</th>
            </tr>
          </thead>
          <tbody>
            ${entryTestRecords
              .map(
                (record) => `
              <tr>
                <td>${record.testType}</td>
                <td>${record.obtainedMarks || "N/A"}</td>
                <td>${record.totalMarks || "N/A"}</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
      </div>
    </body>
    </html>
  `;

    return htmlContent;
  };

  const handleDownloadDocuments = async () => {
    try {
      // Set downloading state to true
      setIsDownloading(true);

      // Show loading indicator or message
      const loadingToast = toast.loading("Preparing documents for download...");

      // Make API call to download documents
      const response = await api.get(
        `/students/download-documents?email=${studentDetails.email}&feeVocherNo=${studentDetails.feeVocherNo}`,
        {
          responseType: "blob", // Important for handling binary data
        }
      );

      // Create a blob from the response data
      const blob = new Blob([response.data], { type: "application/zip" });

      // Use file-saver to save the blob as a file
      saveAs(
        blob,
        `${
          studentDetails.feeVocherNo + "." + studentDetails.email
        }_documents.zip`
      );

      // Update toast to success
      toast.success("Documents downloaded successfully!", { id: loadingToast });
    } catch (error) {
      console.error("Error downloading documents:", error);
      toast.error("Failed to download documents. Please try again.");
    } finally {
      // Set downloading state back to false
      setIsDownloading(false);
    }
  };

  // New function specifically for downloading the student form
  const handleDownloadStudentForm = async () => {
    try {
      // Set downloading form state to true
      setIsDownloadingForm(true);

      // Show loading indicator
      const loadingToast = toast.loading(
        "Preparing student form for download..."
      );

      console.log("Generating student form HTML...");

      // Fetch the student image
      let imageBase64 = "";
      try {
        const imageResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/${studentDetails.image}`
        );
        const imageBlob = await imageResponse.blob();
        imageBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(imageBlob);
        });
      } catch (error) {
        console.error("Error fetching student image:", error);
        // Continue without the image if there's an error
      }

      // Generate the HTML content
      let htmlContent = generateStudentFormHTML();

      // Replace the placeholder with the actual image data
      if (imageBase64) {
        htmlContent = htmlContent.replace(
          "data:image/jpeg;base64,STUDENT_IMAGE_PLACEHOLDER",
          imageBase64
        );
      }

      console.log("HTML content generated successfully");

      // Create a blob with the HTML content
      const blob = new Blob([htmlContent], { type: "text/html" });
      console.log("Blob created successfully");

      // Use file-saver to save the blob as a file
      saveAs(
        blob,
        `${
          studentDetails.feeVocherNo + "." + studentDetails.email
        }_student_form.html`
      );

      console.log("File saved successfully");

      // Update toast to success
      toast.success("Student form downloaded successfully!", {
        id: loadingToast,
      });
    } catch (error) {
      console.error("Error downloading student form:", error);
      toast.error("Failed to download student form. Please try again.");
    } finally {
      // Set downloading form state back to false
      setIsDownloadingForm(false);
    }
  };

  return (
    <>
      {studentDetails == null ? (
        <>
          <h1>no data</h1>
        </>
      ) : (
        <>
          <div className="max-w-7xl mx-auto bg-white shadow-md rounded-lg p-6">
            {/* Back Button */}
            <button
              onClick={() => navigate(-1)}
              className="flex items-center mb-6 px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
              Back
            </button>

            {/* Download Buttons Container */}
            <div className="flex flex-wrap gap-4 mb-6">
              {/* Download Documents Button */}
              <button
                onClick={handleDownloadDocuments}
                disabled={isDownloading}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg shadow-md hover:bg-green-700 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 disabled:bg-green-400 disabled:cursor-not-allowed"
              >
                {isDownloading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Downloading Documents...
                  </>
                ) : (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Download Documents
                  </>
                )}
              </button>

              {/* Download Student Form Button */}
              <button
                onClick={handleDownloadStudentForm}
                disabled={isDownloadingForm}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:bg-blue-400 disabled:cursor-not-allowed"
              >
                {isDownloadingForm ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Downloading Form...
                  </>
                ) : (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Download Student Form
                  </>
                )}
              </button>
            </div>

            {/* Program Applied Section */}
            <div className="bg-blue-800 text-white p-4 rounded-t ">
              <h3 className="text-lg font-semibold">Program Applied For</h3>
            </div>

            <div className="relative overflow-x-auto border dark:border-gray-300 p-6">
              <table className="w-full text-lg text-left rtl:text-right bg-white rounded-lg p-6 p-4">
                <tbody>
                  <tr className="bg-white border-b dark:border-gray-300 border-gray-200 text-lg text-gray-700">
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap font-bold text-sm"
                    >
                      LEVEL
                    </th>
                    <td className="px-6 py-4 text-sm">
                      {studentDetails.level}
                    </td>
                    <td className="px-6 py-4 flex justify-end">
                      <img
                        src={`${process.env.REACT_APP_API_URL}/${studentDetails.image}`}
                        alt="Student"
                        className="w-32 h-32  mx-auto mb-4"
                      />
                    </td>
                  </tr>
                  <tr className="bg-white border-b dark:border-gray-300 border-gray-200 text-lg text-gray-700">
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap font-bold text-sm"
                    >
                      Application No. / Admit Card No.
                    </th>
                    <td className="px-6 py-4 text-sm">
                      {studentDetails.feeVocherNo}
                    </td>
                  </tr>
                  <tr className="bg-white border-b dark:border-gray-300 border-gray-200 text-lg text-gray-700">
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap font-bold text-sm"
                    >
                      PROGRAM (First Choice)
                    </th>
                    <td className="px-6 py-4 text-sm">
                      {studentDetails.program}
                    </td>
                  </tr>
                  <tr className="bg-white border-b dark:border-gray-300 border-gray-200 text-lg text-gray-700">
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap font-bold text-sm"
                    >
                      CURRENT SESSION
                    </th>
                    <td className="px-6 py-4 text-sm">
                      {studentDetails.session}
                    </td>
                  </tr>
                  {/* <tr className="bg-white border-b dark:border-gray-300 border-gray-200 text-lg text-gray-700">
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap font-bold text-sm"
                    >
                      FIRST CHOICE
                    </th>
                    <td className="px-6 py-4 text-sm">
                      {studentDetails.firstChoice}
                    </td>
                  </tr> */}
                  <tr className="bg-white border-b dark:border-gray-300 border-gray-200 text-lg text-gray-700">
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap font-bold text-sm"
                    >
                      SECOND CHOICE
                    </th>
                    <td className="px-6 py-4 text-sm">
                      {studentDetails.secondChoice}
                    </td>
                  </tr>
                  {studentDetails.level === "Undergraduate Programs" && (
                    <>
                      <tr className="bg-white border-b dark:border-gray-300 border-gray-200 text-lg text-gray-700">
                        <th
                          scope="row"
                          className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap font-bold text-sm"
                        >
                          THIRD CHOICE
                        </th>
                        <td className="px-6 py-4 text-sm">
                          {studentDetails.thirdChoice}
                        </td>
                      </tr>
                      <tr className="bg-white border-b dark:border-gray-300 border-gray-200 text-lg text-gray-700">
                        <th
                          scope="row"
                          className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap font-bold text-sm"
                        >
                          FOURTH CHOICE
                        </th>
                        <td className="px-6 py-4 text-sm">
                          {studentDetails.fourthChoice}
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>

            {/* Student's Personal Information */}
            <div className="bg-blue-800 text-white p-4 mt-6 rounded-t ">
              <h3 className="text-lg font-semibold">
                Student's Personal Information
              </h3>
            </div>
            <div className="relative overflow-x-auto border dark:border-gray-300 p-6">
              <table className="w-full text-lg text-left rtl:text-right bg-white rounded-lg p-6 p-4">
                <tbody>
                  <tr className="bg-white border-b dark:border-gray-300 border-gray-200 text-lg text-gray-700">
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap font-bold text-sm"
                    >
                      Name
                    </th>
                    <td className="px-6 py-4 text-sm">{studentDetails.name}</td>
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap text-sm"
                    >
                      Father Name
                    </th>
                    <td className="px-6 py-4 text-sm">
                      {studentDetails.fatherName}
                    </td>
                  </tr>
                  <tr className="bg-white border-b dark:border-gray-300 border-gray-200 text-lg text-gray-700">
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap font-bold text-sm"
                    >
                      Gender
                    </th>
                    <td className="px-6 py-4 text-sm">
                      {studentDetails.gender}
                    </td>
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap text-sm"
                    >
                      Marital Status
                    </th>
                    <td className="px-6 py-4 text-sm">
                      {studentDetails.maritalStatus}
                    </td>
                  </tr>
                  <tr className="bg-white border-b dark:border-gray-300 border-gray-200 text-lg text-gray-700">
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap font-bold text-sm"
                    >
                      CNIC
                    </th>
                    <td className="px-6 py-4 text-sm">{studentDetails.cnic}</td>
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap text-sm"
                    >
                      Mobile No.
                    </th>
                    <td className="px-6 py-4 text-sm">
                      {studentDetails.mobileNo}
                    </td>
                  </tr>
                  <tr className="bg-white border-b dark:border-gray-300 border-gray-200 text-lg text-gray-700">
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap font-bold text-sm"
                    >
                      Date of Birth
                    </th>
                    <td className="px-6 py-4 text-sm">
                      {formatDate(studentDetails.dob)}
                    </td>
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap text-sm"
                    >
                      Email
                    </th>
                    <td className="px-6 py-4 text-sm">
                      {studentDetails.email}
                    </td>
                  </tr>
                  <tr className="bg-white border-b dark:border-gray-300 border-gray-200 text-lg text-gray-700">
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap font-bold text-sm"
                    >
                      Religion
                    </th>
                    <td className="px-6 py-4 text-sm">
                      {studentDetails.religion}
                    </td>
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap text-sm"
                    >
                      Blood Group
                    </th>
                    <td className="px-6 py-4 text-sm">
                      {studentDetails.bloodGroup}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Address & Guardian Information */}
            <div className="bg-blue-800 text-white p-4 mt-6 rounded-t">
              <h3 className="text-lg font-semibold">
                Address & Guardian Information
              </h3>
            </div>
            <div className="relative overflow-x-auto border dark:border-gray-300 p-6">
              <table className="w-full text-lg text-left rtl:text-right bg-white rounded-lg p-6 p-4">
                <tbody>
                  <tr className="bg-white border-b dark:border-gray-300 border-gray-200 text-lg text-gray-700">
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap font-bold text-sm"
                    >
                      Address
                    </th>
                    <td className="px-6 py-4 text-sm">
                      {studentDetails.address}
                    </td>
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap text-sm"
                    >
                      Country
                    </th>
                    <td className="px-6 py-4 text-sm">
                      {studentDetails.country}
                    </td>
                  </tr>
                  <tr className="bg-white border-b dark:border-gray-300 border-gray-200 text-lg text-gray-700">
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap font-bold text-sm"
                    >
                      Province
                    </th>
                    <td className="px-6 py-4 text-sm">
                      {studentDetails.province}
                    </td>
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap text-sm"
                    >
                      City
                    </th>
                    <td className="px-6 py-4 text-sm">{studentDetails.city}</td>
                  </tr>
                  <tr className="bg-white border-b dark:border-gray-300 border-gray-200 text-lg text-gray-700">
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap font-bold text-sm"
                    >
                      Guardian Name
                    </th>
                    <td className="px-6 py-4 text-sm">
                      {studentDetails.guardianName}
                    </td>
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap text-sm"
                    >
                      Guardian Occupation
                    </th>
                    <td className="px-6 py-4 text-sm">
                      {studentDetails.guardianOccupation}
                    </td>
                  </tr>
                  <tr className="bg-white border-b dark:border-gray-300 border-gray-200 text-lg text-gray-700">
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap font-bold text-sm"
                    >
                      Guardian CNIC
                    </th>
                    <td className="px-6 py-4 text-sm">
                      {studentDetails.guardianCnic}
                    </td>
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap text-sm"
                    >
                      Guardian Mobile No.
                    </th>
                    <td className="px-6 py-4 text-sm">
                      {studentDetails.guardianMobileNo}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Academic Background */}
            <div className="bg-blue-800 text-white p-4 mt-6 rounded-t">
              <h3 className="text-lg font-semibold">Academic Background</h3>
            </div>
            <div className="mt-4">
              <table className="min-w-full table-auto">
                <thead className="bg-gray-100 ">
                  <tr>
                    <th className="p-4 text-left">Degree</th>
                    <th className="p-4 text-left">Major Subjects</th>
                    <th className="p-4 text-left">Total Marks</th>
                    <th className="p-4 text-left">Obtained Marks</th>
                    <th className="p-4 text-left">Percentage</th>
                    <th className="p-4 text-left">Year</th>
                    <th className="p-4 text-left">Board/Institution</th>
                  </tr>
                </thead>
                <tbody>
                  {academicRecords.map((record, index) => (
                    <tr key={index} className="border-t text-sm">
                      <td className="p-4  font-medium text-gray-900 whitespace-nowrap font-bold text-sm">
                        {record.degree}
                      </td>
                      <td className="p-4">{record.majorSubjects}</td>
                      <td className="p-4">{record.totalMarks}</td>
                      <td className="p-4">{record.obtainedMarks}</td>
                      <td className="p-4">{record.percentage}%</td>
                      <td className="p-4">{record.passingYear}</td>
                      <td className="p-4">{record.boardInstitution}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Entry Test Results */}
            <div className="bg-blue-800 text-white p-4 mt-6 rounded-t">
              <h3 className="text-lg font-semibold">Entry Test Result</h3>
            </div>
            <div className="mt-4">
              <table className="min-w-full table-auto">
                <thead className="bg-gray-100 ">
                  <tr>
                    <th className="p-4 text-left">Test Type</th>
                    <th className="p-4 text-left">Obtained Marks</th>
                    <th className="p-4 text-left">Total Marks</th>
                  </tr>
                </thead>
                <tbody>
                  {entryTestRecords.length > 0 ? (
                    entryTestRecords.map((record, index) => (
                      <tr key={index} className="border-t text-sm">
                        <td className="p-4 font-medium text-gray-900 whitespace-nowrap font-bold text-sm">
                          {record.testType}
                        </td>
                        <td className="p-4">{record.obtainedMarks || "N/A"}</td>
                        <td className="p-4">{record.totalMarks || "N/A"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr className="border-t text-sm">
                      <td colSpan="3" className="p-4 text-center text-gray-500">
                        No entry test records available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Fee Voucher */}
            <div className="bg-blue-800 text-white p-4 mt-6 rounded-t">
              <h3 className="text-lg font-semibold">Fee Voucher</h3>
            </div>
            <div className="mt-4">
              <img
                style={{ height: "600px", width: "auto" }}
                src={`${process.env.REACT_APP_API_URL}/${
                  studentDetails.fee_voucher && studentDetails.fee_voucher
                }`}
                alt="Voucher hasn't been updated yet"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ShowStudentForm;
